/**
 * Ask a /desktop page already on screen to show one desktop session (2026-10-06, PR 4 of the DocuSign fix).
 *
 * "Open the live desktop" in the sign-in window puts the person on the /desktop page in this tab. When that page
 * is already mounted (the sign-in window was opened over it), a change of address alone does not tell it which
 * session to show: react-router keeps the same page mounted. So the window asks here first. The page answers on
 * the event itself:
 *   - 'showing': it shows that session (or already did);
 *   - 'other-machine': the session lives on another workstation than the one the page shows, so it cannot.
 * No answer (null) means no /desktop page is mounted: the window navigates there, and the page reads the session
 * from its address when it mounts. A page that finds the session on another machine only after it mounted says so
 * with the answer event below.
 *
 * Imports nothing: both the sign-in window's chunk and the desktop page read it.
 */
export const DESKTOP_SHOW_SESSION_EVENT = 'prest0n:desktop-show-session';
export const DESKTOP_SHOW_ANSWER_EVENT = 'prest0n:desktop-show-session-answer';

export type DesktopShowAnswer = 'showing' | 'other-machine';

export interface DesktopShowSessionDetail {
  sessionId: string;
  /** The service base the session lives on, as the sign-in window reaches it. */
  base: string | null;
  /** Written by the page that took the request. */
  answer?: DesktopShowAnswer;
}

/** One of our workstations' hosts: a dashed-IP sslip.io name (`1-2-3-4.sslip.io`). */
const WORKSTATION_HOST_RE = /^(?:\d{1,3}-){3}\d{1,3}\.sslip\.io$/i;

/** The workstation a service base is on (its host), or null when the base names none of ours. */
export function workstationHostOf(base: string | null | undefined): string | null {
  if (typeof base !== 'string' || !base.trim()) return null;
  try {
    const url = new URL(base.trim());
    if (url.protocol !== 'https:') return null;
    const host = url.hostname.toLowerCase();
    return WORKSTATION_HOST_RE.test(host) ? host : null;
  } catch {
    return null;
  }
}

/**
 * True only when both bases name one of our workstations and they are different machines. A base of any other
 * shape is not evidence of anything: the page then tries the session, and its own owner check decides. The shared
 * service (`…/browser`) and a page's own base (`…`) on one host are one machine.
 */
export function onDifferentMachines(a: string | null | undefined, b: string | null | undefined): boolean {
  const hostA = workstationHostOf(a);
  const hostB = workstationHostOf(b);
  return !!hostA && !!hostB && hostA !== hostB;
}

/** The page's answer, or null when no /desktop page is mounted. */
export function requestDesktopShowSession(sessionId: string, base: string | null): DesktopShowAnswer | null {
  if (typeof document === 'undefined' || typeof CustomEvent === 'undefined') return null;
  try {
    const detail: DesktopShowSessionDetail = { sessionId, base };
    const event = new CustomEvent<DesktopShowSessionDetail>(DESKTOP_SHOW_SESSION_EVENT, { cancelable: true, detail });
    document.dispatchEvent(event);
    if (!event.defaultPrevented) return null;
    return detail.answer === 'other-machine' ? 'other-machine' : 'showing';
  } catch {
    return null;
  }
}

/** The page says, after the fact, what became of a session it was sent to show. */
export function announceDesktopShowAnswer(answer: { sessionId: string; answer: DesktopShowAnswer }): void {
  if (typeof document === 'undefined' || typeof CustomEvent === 'undefined') return;
  try {
    document.dispatchEvent(new CustomEvent(DESKTOP_SHOW_ANSWER_EVENT, { detail: { ...answer } }));
  } catch {
    // Nobody listening is not an error.
  }
}

/** Hear the page's answers; returns the unsubscribe. */
export function onDesktopShowAnswer(fn: (answer: { sessionId: string; answer: DesktopShowAnswer }) => void): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const handler = (e: Event) => {
    const d = (e as CustomEvent).detail as { sessionId?: unknown; answer?: unknown } | undefined;
    if (!d || typeof d.sessionId !== 'string') return;
    if (d.answer !== 'showing' && d.answer !== 'other-machine') return;
    fn({ sessionId: d.sessionId, answer: d.answer });
  };
  document.addEventListener(DESKTOP_SHOW_ANSWER_EVENT, handler);
  return () => document.removeEventListener(DESKTOP_SHOW_ANSWER_EVENT, handler);
}
